import React, { useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { PopupActions } from 'reactjs-popup/dist/types';
import { z } from 'zod';

import {
  isValidPythonVariable,
  upCaseFirstLetter,
} from '@polyai/common/lib/stringHelper';
import Button from '@polyai/ui/components/atoms/Button';
import Input from '@polyai/ui/components/atoms/Input';
import { Body, Title } from '@polyai/ui/components/atoms/LegacyText';
import { Stack } from '@polyai/ui/components/atoms/Stack';
import type { ModalProps } from '@polyai/ui/components/molecules/LegacyModal';
import LegacyModal, {
  ModalButtons,
} from '@polyai/ui/components/molecules/LegacyModal';

import FormProvider from 'components/molecules/Form';
import type { ModalType } from './VariantModal.copy';
import { VARIANT_MODAL_COPY } from './VariantModal.copy';

interface Props extends Omit<ModalProps, 'children'> {
  id?: string;
  originalName?: string;
  existingNames: string[];
  type: ModalType;
  fieldConfig: Record<string, { label: string; value: string }>;
  readonly?: boolean;
  onSubmit: (
    key: string,
    newValues: Record<string, string>,
    id?: string,
  ) => void;
  isSubmitting?: boolean;
}

const NAME_FIELD = 'name';

const VariantModal: React.FC<Props> = ({
  id,
  originalName,
  existingNames,
  type,
  fieldConfig,
  onSubmit,
  isSubmitting,
  readonly,
  ...rest
}) => {
  const popupRef = useRef<PopupActions>(null);
  const [isModalOpen, setIsModalOpen] = React.useState(rest.open ?? false);

  const isEdit = !!id;

  const defaultValues = Object.keys(fieldConfig).reduce(
    (values, id) => ({
      ...values,
      [id]: fieldConfig[id].value,
      [NAME_FIELD]: originalName ?? '',
    }),
    {} as Record<string, string>,
  );

  const schema = z.object(
    Object.keys(defaultValues).reduce(
      (acc, key) => {
        if (key === NAME_FIELD) {
          return acc;
        }
        acc[key] = z.string();
        return acc;
      },
      {
        [NAME_FIELD]: z
          .string()
          .min(1, `${NAME_FIELD} is required`)
          .refine(
            (name: string) => {
              if (name !== originalName && existingNames.includes(name)) {
                return false;
              }
              return true;
            },
            {
              message: `${upCaseFirstLetter(type)} name must be unique`,
            },
          )
          .refine(
            (name: string) => {
              if (type === 'attribute' && !isValidPythonVariable(name)) {
                return false;
              }
              return true;
            },
            {
              message: `Name must be a valid Python variable`,
            },
          ) as unknown as z.ZodString,
      } as Record<string, z.ZodString>,
    ),
  );
  type FormProps = z.infer<typeof schema>;
  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    shouldUnregister: true,
  });

  const {
    register,
    formState: { errors },
  } = methods;

  const submitForm: SubmitHandler<FormProps> = newValues => {
    const { name, ...values } = newValues;
    if (!name) {
      throw new Error('Should not get here, key and id are undefined');
    }

    onSubmit(name, values, id);
    popupRef.current?.close();
  };

  return (
    <LegacyModal
      {...rest}
      ref={popupRef}
      onClose={() => setIsModalOpen(false)}
      onOpen={() => setIsModalOpen(true)}
    >
      {isModalOpen && (
        <FormProvider
          defaultValues={defaultValues}
          methods={methods}
          onSubmit={submitForm}
        >
          <Stack
            aria-label={VARIANT_MODAL_COPY[type].title[isEdit ? 'edit' : 'add']}
            data-test-id={`${type}-modal`}
            direction="column"
            role="dialog"
            spacing="sm4"
          >
            <Stack direction="column" spacing="sm1">
              <Title>
                {VARIANT_MODAL_COPY[type].title[isEdit ? 'edit' : 'add']}
              </Title>
              <Body>
                {VARIANT_MODAL_COPY[type].description[isEdit ? 'edit' : 'add']}
              </Body>
            </Stack>

            <Stack direction="column" spacing="sm3">
              <Input
                key={NAME_FIELD}
                autoComplete="off"
                label={`${upCaseFirstLetter(type)} name`}
                readOnly={readonly}
                fullWidth
                {...register(NAME_FIELD)}
                errors={
                  errors?.[NAME_FIELD]?.message
                    ? [errors?.[NAME_FIELD]?.message]
                    : undefined
                }
              />
              {Object.keys(defaultValues).map(
                field =>
                  field !== NAME_FIELD && (
                    <Input
                      key={field}
                      label={fieldConfig[field].label}
                      fullWidth
                      {...register(field)}
                      errors={
                        errors[field]?.message
                          ? [errors[field]?.message]
                          : undefined
                      }
                      readOnly={readonly}
                      textArea
                    />
                  ),
              )}
            </Stack>

            <ModalButtons>
              <Button
                label={readonly ? 'Close' : 'Cancel'}
                type="button"
                variant="SECONDARY"
                onClick={() => popupRef.current?.close()}
              />
              {!readonly && (
                <Button
                  data-test-id="save-btn"
                  label="Save"
                  loading={isSubmitting}
                />
              )}
            </ModalButtons>
          </Stack>
        </FormProvider>
      )}
    </LegacyModal>
  );
};

export default VariantModal;
